<!-- Copyright (c) 2025 Digital Systems (M) Sdn. Bhd. -->
<template>
  <div class="item-type-main">
    <div>
      <Menus :menu="menu" @menuClicked="menuClicked" />
    </div>
    <div class="table-box-container">
      <TableBox
        title="Item Type"
        :tableData="itemTypeListMaster"
        :maxSelection="1"
        :selectedRow.sync="itemTypeTableBoxSelectedRow"
        :loading="itemTypeTableBoxLoading"
      />
    </div>

    <PopupEntry
      :show.sync="showPopupEntry"
      v-if="showPopupEntry"
      :title="popupEntryTitle"
      :updatedBy="updatedBy"
      :createdBy="createdBy"
      @save="popupEntrySaveClicked"
      @requiredClicked="popupEntryRequiredClicked"
      :requiredError="popupEntryRequiredError"
      :successMessages="popupEntrySuccessMessages"
      :viewMode="viewMode"
      :errorMessages="popupEntryErrorMessages"
      :cancelButton="viewMode ? 'Close' : 'Cancel'"
    >
      <div>
        <div>
          <Input
            placeholder="Item Type"
            :inputValue.sync="itemType"
            :isRequired="true"
            :isDisabled="viewModePK"
            :errorMessage.sync="itemTypeErr"
            ref="itemTypeErr"
            validator="masterfilecode,10,uppercase"
          />
        </div>
        <Input
          placeholder="Item Description"
          :inputValue.sync="itemTypeDescription"
          :isRequired="true"
          :errorMessage.sync="itemTypeDescriptionErr"
          :isDisabled="viewMode"
          validator="text,100"
          ref="itemTypeDescriptionErr"
        />
        <DateTimePicker
          placeholder="Effective Date"
          :isRequired="false"
          :showTime="false"
          :isDisabled="viewMode"
          :errorMessage.sync="itemTypeEffectiveDateErr"
          :value.sync="itemTypeEffectiveDate"
          ref="itemTypeEffectiveDateErr"
        />
        <DateTimePicker
          placeholder="Expiry Date"
          :isRequired="false"
          :showTime="false"
          :isDisabled="viewMode"
          :errorMessage.sync="itemTypeExpiryDateErr"
          :value.sync="itemTypeExpiryDate"
          ref="itemTypeExpiryDateErr"
        />
      </div>
    </PopupEntry>
  </div>
</template>

<script>
export default {
  components: {},
  computed: {},
  data: function () {
    return {
      itemTypeListMaster: [], // all data pulled from API
      itemType: '',
      itemTypeErr: '',
      itemTypeDescription: '',
      itemTypeEffectiveDate: null,
      itemTypeExpiryDate: null,
      itemTypeDescriptionErr: '',
      itemTypeEffectiveDateErr: '',
      itemTypeExpiryDateErr: '',

      menu: [
        {
          title: 'New Item Type',
          icon: 'https://dsmapi.logistics.systems/dsm6gicons/icons8-add-new-50.png',
          key: 'MnuNew',
          visible: true,
          enabled: false,
        },
        {
          title: 'Edit Item Type',
          icon: 'https://dsmapi.logistics.systems/dsm6gicons/icons8-edit-64%20(1).png',
          key: 'MnuEdit',
          visible: true,
          enabled: false,
        },
        {
          title: 'Delete Item Type',
          icon: 'https://dsmapi.logistics.systems/dsm6gicons/icons8-reduce-50.png',
          key: 'MnuDelete',
          visible: true,
          enabled: false,
        },
        {
          title: 'View Item Type',
          icon: 'https://dsmapi.logistics.systems/dsm6gicons/icons8-view-64.png',
          key: 'MnuView',
          visible: true,
          enabled: false,
        },
      ],

      itemTypeTableBoxSelectedRow: [],
      itemTypeTableBoxLoading: false,
      showPopupEntry: false,
      popupEntryTitle: '',
      updatedBy: '',
      createdBy: '',
      viewMode: false,
      viewModePK: false,
      popupEntryRequiredError: [],
      popupEntrySuccessMessages: [],
      popupEntryErrorMessages: [],
    };
  },
  props: {},
  methods: {
    getItemTypeListMaster: async function () {
      this.itemTypeListMaster = []; // to reset table data
      this.itemTypeTableBoxSelectedRow = []; // empties the selected row during refresh
      this.itemTypeTableBoxLoading = true;
      this.itemTypeListMaster = await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'ITEMTYPE' });
      this.itemTypeTableBoxLoading = false;
    },

    menuClicked: async function (e) {
      this.clearPopupEntryMessages();
      this.resetPopupEntryFields();

      switch (e.title) {
        case 'New Item Type':
          this.popupEntryTitle = 'New Item Type';
          this.viewMode = false;
          this.viewModePK = false;
          this.showPopupEntry = true;
          break;
        case 'Edit Item Type':
          if (this.itemTypeTableBoxSelectedRow.length == 0) {
            this.$showAlert('error', 'No Item Type is selected', 'Please select an Item Type to edit');
            break;
          }
          this.$fullscreenLoading('Loading Item Type to Edit');
          await this.populatePopupEntryFields(this.itemTypeTableBoxSelectedRow[0]['Item Type']);
          this.$fullscreenLoading('');
          this.viewMode = false;
          this.viewModePK = true;
          this.showPopupEntry = true;
          this.popupEntryTitle = 'Edit Item Type';
          break;

        case 'Delete Item Type': {
          if (this.itemTypeTableBoxSelectedRow.length == 0) {
            await this.$showAlert('error', 'No Item Type is selected', 'Please select an Item Type to delete');
            break;
          }
          this.populatePopupEntryFields(this.itemTypeTableBoxSelectedRow[0]['Item Type']);
          const promptMessage = 'Are you sure you want to delete ' + this.itemTypeTableBoxSelectedRow[0]['Item Type'];
          const deleteConfirmation = await this.$showAlert('warning', 'Delete Item Type', promptMessage, [
            { text: 'Delete', textColor: 'white', color: 'red' },
            { text: 'Cancel' },
          ]);
          if (deleteConfirmation == 'Cancel') {
            break;
          }
          this.$fullscreenLoading('Deleting Item Type');
          const result = await this.$apiCallV2('/ItemType/SaveItemType', this.formatSaveItemTypeRequestBody('D'));
          this.$fullscreenLoading('');

          if (result[0]['Results'] != '0') {
            await this.$showAlert(
              'error',
              'Item Type NOT Deleted',
              `There was an error deleting Item Type ${this.itemTypeTableBoxSelectedRow[0]['Item Type']}`,
            );
            break;
          } else {
            await this.$showAlert(
              'success',
              'Item Type Deleted',
              `Successfully deleted Item Type ${this.itemTypeTableBoxSelectedRow[0]['Item Type']}`,
            );
            this.getItemTypeListMaster();
            break;
          }
        }

        case 'View Item Type':
          if (this.itemTypeTableBoxSelectedRow.length == 0) {
            this.$showAlert('error', 'No Item Type is selected', 'Please select an Item Type to view');
            break;
          }
          this.populatePopupEntryFields(this.itemTypeTableBoxSelectedRow[0]['Item Type']);
          this.$fullscreenLoading('Loading Item type details');
          this.popupEntryTitle = 'View Item Type';
          this.$fullscreenLoading('');
          this.viewMode = true;
          this.viewModePK = true;
          this.showPopupEntry = true;
          break;
      }
    },

    clearPopupEntryMessages: function () {
      this.popupEntryRequiredError = [];
      this.popupEntrySuccessMessages = [];
      this.popupEntryErrorMessages = [];
    },

    resetPopupEntryFields: function () {
      this.popupEntryTitle = '';
      this.createdBy = '';
      this.updatedBy = '';
      this.itemType = '';
      this.itemTypeErr = '';
      this.activeIsChecked = true;
      this.itemTypeDescription = '';
      this.itemTypeDescriptionErr = '';
      this.itemTypeEffectiveDate = null;
      this.itemTypeExpiryDate = null;
      this.itemTypeEffectiveDateErr = '';
      this.itemTypeExpiryDateErr = '';
    },

    checkPopupEntryFields: function () {
      let error = false;

      this.clearPopupEntryMessages();
      if (this.itemType.trim() === '') {
        this.itemTypeErr = 'Item Type is required!';
        this.popupEntryRequiredError.push({ key: 'itemTypeErr', value: this.itemTypeErr });
        error = true;
      } else if (this.itemType.length > 10) {
        this.itemTypeErr = 'Item Type cannot exceed length of 10 characters';
        this.popupEntryRequiredError.push({ key: 'itemTypeErr', value: this.itemTypeErr });
        error = true;
      }

      if (this.itemTypeDescription.trim() === '') {
        this.itemTypeDescriptionErr = 'Item description is required!';
        this.popupEntryRequiredError.push({ key: 'itemTypeDescriptionErr', value: this.itemTypeDescriptionErr });
        error = true;
      } else if (this.itemTypeDescription.length > 100) {
        this.itemTypeDescriptionErr = 'Item description cannot exceed 100 characters!';
        this.popupEntryRequiredError.push({
          key: 'itemTypeDescriptionErr',
          value: this.itemTypeDescriptionErr,
        });
        error = true;
      }

      // added date filter block for invalid date entries
      if (this.itemTypeEffectiveDate && this.itemTypeEffectiveDate <= new Date('1900-01-01')) {
        this.itemTypeEffectiveDateErr = 'Effective date input is invalid';
        this.popupEntryRequiredError.push({ key: 'itemTypeEffectiveDateErr', value: this.itemTypeEffectiveDateErr });
        error = true;
      }
      if (this.itemTypeExpiryDate && this.itemTypeExpiryDate <= new Date('1900-01-01')) {
        this.itemTypeExpiryDateErr = 'Expiry date input is invalid';
        this.popupEntryRequiredError.push({ key: 'itemTypeExpiryDateErr', value: this.itemTypeExpiryDateErr });
        error = true;
      }
      if (this.itemTypeExpiryDate <= this.itemTypeEffectiveDate){
        this.itemTypeExpiryDateErr = 'Expiry Date must be later than effective date';
        this.popupEntryRequiredError.push({ key: 'itemTypeExpiryDateErr', value: this.itemTypeExpiryDateErr });
        error = true;
      }
      return error;
    },

    popupEntrySaveClicked: async function () {
      const isInvalid = this.checkPopupEntryFields();
      if (isInvalid) {
        return;
      }
      const action = this.popupEntryTitle[0];
      const requestBody = this.formatSaveItemTypeRequestBody(action);
      this.$fullscreenLoading('Saving Item Type');
      const result = await this.$apiCallV2('/ItemType/SaveItemType', requestBody);
      this.$fullscreenLoading('');
      if (result[0]['Results'] != '0') {
        if (action == 'N') {
          this.popupEntryErrorMessages.push(`There was an error creating Item Type: ${result[0]['ErrMsg']}`);
        } else {
          this.popupEntryErrorMessages.push(`There was an error editing Item Type: ${result[0]['ErrMsg']}`);
        }
        return;
      }
      this.viewMode = true;
      this.viewModePK = true;
      this.popupEntryTitle = 'View Item Type';
      // this.resetPopupEntryFields();
      this.getItemTypeListMaster();
      if (action == 'N') {
        this.popupEntrySuccessMessages.push(`Successfully created Item Type: ${result[0]['ErrMsg']}`);
      } else {
        this.popupEntrySuccessMessages.push(`Successfully edited Item Type: ${result[0]['ErrMsg']}`);
      }
      this.populatePopupEntryFields(this.itemType);
    },

    formatSaveItemTypeRequestBody: function (action) {
      const requestBody = {
        itemtype: this.itemType.trim(),
        description: this.itemTypeDescription,
        effectivedate: this.itemTypeEffectiveDate?.formatString?.('yyyy-MM-dd') ?? null,
        expirydate: this.itemTypeExpiryDate?.formatString?.('yyyy-MM-dd') ?? null,
        action: action,
      };
      return requestBody;
    },

    populatePopupEntryFields: async function (itemTypeDetails) {
      const details = await this.$apiCallV2('/Master/GetDetailsMaster', {
        masterfile: 'ITEMTYPE',
        recordid: itemTypeDetails,
      });
      this.itemType = details['ItemType']['Item Type'];
      this.itemTypeDescription = details['ItemType']['Description'];
      if (details['ItemType']['Effective Date']) {
        this.itemTypeEffectiveDate = new Date(details['ItemType']['Effective Date']);
      }
      if (details['ItemType']['Expiry Date']) {
        this.itemTypeExpiryDate = new Date(details['ItemType']['Expiry Date']);
      }
    },

    popupEntryRequiredClicked: function (item) {
      this.$nextTick(() => {
        this.$refs[item].setFocus();
      });
    },
  },
  mounted() {
    this.$bus.$on('pageSASControlAccess', (x) => {
      if (x.page == 'ItemType') {
        x.data.forEach((k) => {
          const selectedMenu = this.menu.find((m) => m['key'] == k['Control']);
          if (selectedMenu) {
            selectedMenu[k['Properties'].toLowerCase()] = k['PropertiesValue'] == 'True';
          }
        });
      }
    });

    const mountedPromises = [];
    let promisesFulfilled = 0;

    mountedPromises.push(
      this.getItemTypeListMaster().then(() => {
        this.$topLoading(++promisesFulfilled / mountedPromises.length); // top loading bar won't appear as there is only 1 API call,jumps directly from 0% to 100%
      }),
    );

    Promise.allSettled(mountedPromises).then((_promseOutcomes) => {
      this.menu = this.menu.map((menu) => ({
        ...menu,
        enabled: true,
      }));
    });
  },
  watch: {},
};
</script>

<style lang="scss" scoped>
@import '@/css/all.scss';
.item-type-main {
  font-size: 12px;
  width: 100%;
  height: 100%;
  padding: 10px 45px;
  display: flex;
  > .table-box-container {
    width: 100%;
    margin-left: 5px;
    padding: 5px;
    background-color: white;
    border-radius: 5px;
    display: flex;
    overflow: hidden;
  }
}
</style>
